"use client";

import { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import { getActivePromo } from "@/utils/fees";
import { countryName, toA2 } from "@/features/landing/utils/country-name";
import AmountSelector from "./products/AmountSelector";
import PiSummary from "./products/PiSummary";
import CryptoHint from "./products/CryptoHint";
import {
  buildEsimAutoDetails,
  buildVoucherAutoDetails,
} from "./products/productCardDetails";

const slugify = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function getDetailHref({ typeUpper, brand, country, offerId }) {
  if (typeUpper === "VOUCHER" && brand && country) {
    return `/vouchers/${encodeURIComponent(slugify(brand))}/${encodeURIComponent(
      String(country).toLowerCase()
    )}`;
  }
  if (typeUpper === "VOUCHER" && offerId) {
    return `/vouchers/offer/${encodeURIComponent(offerId)}`;
  }
  if (typeUpper === "TOPUP" && offerId) {
    return `/topups/offer/${encodeURIComponent(offerId)}`;
  }
  if (typeUpper === "ESIM" && offerId) {
    return `/esims/offer/${encodeURIComponent(offerId)}`;
  }
  return "#";
}
function Header({ displayTitle, offerId, detailHref }) {
  return (
    <div className="mb-2 flex items-start justify-between gap-2">
      <h2 className="line-clamp-2 text-lg font-semibold leading-snug">📦 {displayTitle}</h2>

      {!!offerId && (
        <Link
          href={detailHref}
          className="shrink-0 rounded-full border border-border bg-background/30 px-2 py-1 text-xs hover:bg-background/50"
          onClick={(e) => e.stopPropagation()}
        >
          View →
        </Link>
      )}
    </div>
  );
}

function Badges({
  isVoucher,
  isVisa,
  isVisaAwards,
  isRange,
  subTypes,
  promo,
}) {
  return (
    <>
      {isVoucher && isVisa && (
        <div className="mb-2 flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full border border-border bg-background/30 px-2 py-1 text-[10px] text-textPrimary">
            {isVisaAwards ? "Visa Awards (6M)" : "Visa Prepaid"}
          </span>

          {isRange && (
            <span className="inline-flex items-center rounded-full border border-border bg-background/30 px-2 py-1 text-[10px] text-textPrimary">
              Range
            </span>
          )}
        </div>
      )}

      {subTypes?.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
          {subTypes.map((sub) => (
            <Link
              key={sub}
              href={isVoucher ? `/vouchers/category/${slugify(sub)}` : "#"}
              className="rounded-full border border-border bg-background/30 px-2 py-1 text-xs font-medium text-textPrimary hover:underline"
            >
              {sub}
            </Link>
          ))}
        </div>
      )}

      {promo && (
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
          🎉 {promo.name} — {Math.round((1 - promo.fee) * 100)}% Off!
        </div>
      )}
    </>
  );
}

function PriceBlock({ priceText, isRange, priceCurrency, localValueDisplay, localCurrency }) {
  return (
    <>
      <div className="mb-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tracking-tight text-textPrimary">{priceText}</span>
        {isRange && priceCurrency && (
          <span className="text-[11px] text-textSecondary">({priceCurrency})</span>
        )}
      </div>

      {localValueDisplay && (
        <p className="mb-1 text-xs text-textSecondary">
          Local value: <span className="font-medium text-textPrimary">{localValueDisplay}</span>
          {localCurrency ? (
            <span className="ml-1 text-[10px] text-textSecondary">({localCurrency})</span>
          ) : null}
        </p>
      )}
    </>
  );
}

function DetailRows({ rows }) {
  if (!rows?.length) return null;

  return (
    <div className="mt-2 space-y-1 text-xs text-textPrimary">
      {rows.map((item) => (
        <p key={item.label}>
          {item.icon} {item.label}: <strong>{item.value}</strong>
        </p>
      ))}
    </div>
  );
}

function NotesBlock({
  shortNotes,
  notes,
  showFullNotes,
  setShowFullNotes,
}) {
  if (!shortNotes && !notes) return null;

  return (
    <div className="mt-2 text-xs text-textPrimary">
      {shortNotes ? <p className="mb-1">{shortNotes}</p> : null}

      {notes ? (
        <>
          {!showFullNotes ? (
            <p className="line-clamp-3 text-textPrimary">{notes}</p>
          ) : (
            <p className="whitespace-pre-line text-textPrimary">{notes}</p>
          )}

          <button
            type="button"
            className="mt-1 text-primary hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullNotes((s) => !s);
            }}
          >
            {showFullNotes ? "Show less" : "Read more"}
          </button>
        </>
      ) : null}
    </div>
  );
}

function ExpandedDetails({
  isDetailsVisible,
  productType,
  recipientGets,
  sendCurrency,
  topupSubtype,
  isEsim,
  detailsArray,
  voucherAutoDetails,
  shortNotes,
  notes,
  showFullNotes,
  setShowFullNotes,
  country,
  roamingCount,
  roamingCountries,
  regions,
}) {
  if (!isDetailsVisible) return null;

  const coverageNames =
    Array.isArray(roamingCountries) && roamingCountries.length
      ? roamingCountries.map((c) => countryName(toA2(c))).filter(Boolean)
      : [];

  const isTopup = String(productType).toUpperCase() === "TOPUP";

  return (
    <>
      {isTopup && !!recipientGets && !!sendCurrency && (
        <div className="mt-2 text-xs leading-snug text-textPrimary">
          {topupSubtype && (
            <span className="mb-2 inline-flex items-center rounded-full border border-border bg-background/30 px-2 py-1 text-xs font-medium text-textPrimary">
              {topupSubtype}
            </span>
          )}
          <p>
            Recipient Gets: <strong>{recipientGets}</strong>
            <span className="ml-2 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs text-white">
              {sendCurrency}
            </span>
          </p>
        </div>
      )}

      {!isEsim && detailsArray?.length > 0 && <DetailRows rows={detailsArray} />}
      {!detailsArray && <DetailRows rows={voucherAutoDetails} />}

      <NotesBlock
        shortNotes={shortNotes}
        notes={notes}
        showFullNotes={showFullNotes}
        setShowFullNotes={setShowFullNotes}
      />

      <div className="mt-2 space-y-1 text-[11px] text-textPrimary">
        {country ? <p>Country: {country}</p> : null}
        {!country && (coverageNames.length > 0 || Number(roamingCount) > 0) ? (
          <p>
            Coverage: {regions?.[0] || "Regional"}
            {coverageNames.length > 0
              ? `: ${coverageNames.join(", ")}`
              : ` • ${Number(roamingCount)} countries`}
          </p>
        ) : null}
        {regions?.length ? <p>Regions: {regions.join(", ")}</p> : null}
      </div>
    </>
  );
}

export default function ProductCard(props) {
  const {
    offerId,
    name,
    image,
    priceUSD,
    priceDisplay,
    priceStep,
    brand,
    hasRange = false,
    priceType,
    priceMinValue = null,
    priceMaxValue = null,
    priceMinDisplay = null,
    priceMaxDisplay = null,
    priceCurrency = "USD",
    roamingCount = 0,
    roamingCountries = [],
    basePI,
    totalPI,
    feePercent,
    piRate,
    productType,
    onAmountChange,
    localValueDisplay,
    localCurrency,
    showCryptoHint = false,
    durationDays,
    dataGB,
    dataUnlimited,
    dataSpeeds = [],
    subTypes = [],
    recipientGets,
    sendCurrency,
    topupSubtype,
    faceCurrency,
    details,
    country,
    regions = [],
    shortNotes,
    notes,
    selectedAmount: controlledSelectedAmount,
    onSelectedAmountChange,
    brandName,
    uiBrand,
    uiTitle,
    onClick,
  } = props;

  const typeUpper = String(productType || "").toUpperCase();
  const isVoucher = typeUpper === "VOUCHER";
  const isEsim = typeUpper === "ESIM";
  const isRange = String(priceType || "").toUpperCase() === "RANGE" || !!hasRange;

  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [showFullNotes, setShowFullNotes] = useState(false);

  const [internalAmount, setInternalAmount] = useState(() =>
    isRange && Number.isFinite(Number(priceMinValue)) ? Number(priceMinValue) : null
  );

  const isControlled = controlledSelectedAmount !== undefined;
  const selectedAmount = isControlled ? controlledSelectedAmount : internalAmount;

  const setSelectedAmount = (next) => {
    const resolved = typeof next === "function" ? next(selectedAmount) : next;
    if (isControlled) onSelectedAmountChange?.(resolved);
    else setInternalAmount(resolved);
  };

  const min = Number(priceMinValue) || 0;
  const max = Number(priceMaxValue) || min;

  useEffect(() => {
    if (!isRange) return;

    const minV = Number(priceMinValue);
    const maxV = Number(priceMaxValue);
    if (!Number.isFinite(minV)) return;

    const clampToRange = (prev) => {
      if (Number.isFinite(prev)) {
        return Math.max(minV, Number.isFinite(maxV) ? Math.min(maxV, prev) : prev);
      }
      return minV;
    };

    if (isControlled) {
      const next = clampToRange(selectedAmount);
      if (next !== selectedAmount) onSelectedAmountChange?.(next);
    } else {
      setInternalAmount((prev) => clampToRange(prev));
    }
  }, [isRange, priceMinValue, priceMaxValue, isControlled, selectedAmount, onSelectedAmountChange]);

  useEffect(() => {
    if (!isRange) return;
    if (Number.isFinite(selectedAmount)) onAmountChange?.(selectedAmount);
  }, [isRange, selectedAmount, onAmountChange]);

  const detailHref = getDetailHref({ typeUpper, brand, country, offerId });
  const detailsArray = Array.isArray(details) ? details : null;
  const imgSrc = image || "/assets/reloadpi-esim.svg";

  const displayTitle = useMemo(() => {
    if (uiTitle) return uiTitle;
    if (isEsim) {
      const rawBrand = (brandName || uiBrand || "").trim();
      if (rawBrand && /esim/i.test(rawBrand)) return rawBrand;
      return `${rawBrand || "ReloadPi"} eSIM`;
    }
    return name || "Unnamed Product";
  }, [uiTitle, isEsim, brandName, uiBrand, name]);

  const isVisa = useMemo(() => {
    const inText = (s) => typeof s === "string" && /visa/i.test(s);
    const inDetails =
      Array.isArray(detailsArray) &&
      detailsArray.some((d) => inText(d?.label) || inText(d?.value));
    const inSubTypes = Array.isArray(subTypes) && subTypes.some((s) => /visa/i.test(String(s)));
    return inText(name) || inText(offerId) || inSubTypes || inDetails;
  }, [name, offerId, subTypes, detailsArray]);

  const isVisaAwards = useMemo(() => {
    const hasAwardsToken =
      /AWARDS/i.test(offerId || "") ||
      (Array.isArray(subTypes) && subTypes.some((s) => /awards/i.test(String(s))));
    return isVisa && hasAwardsToken;
  }, [isVisa, offerId, subTypes]);

  const esimsAutoDetails = useMemo(
  () =>
    buildEsimAutoDetails({
      isEsim,
      detailsArray,
      durationDays,
      dataGB,
      dataUnlimited,
      dataSpeeds,
      country,
      roamingCount,
      roamingCountries,
      regions,
    }),
  [
    isEsim,
    detailsArray,
    durationDays,
    dataGB,
    dataUnlimited,
    dataSpeeds,
    country,
    roamingCount,
    roamingCountries,
    regions,
  ]
);

const voucherAutoDetails = useMemo(
  () =>
    buildVoucherAutoDetails({
      isVoucher,
      isVisa,
      detailsArray,
      isVisaAwards,
      isRange,
      priceMinDisplay,
      priceMaxDisplay,
      priceDisplay,
      priceCurrency,
      faceCurrency,
      country,
      regions,
    }),
  [
    isVoucher,
    isVisa,
    detailsArray,
    isVisaAwards,
    isRange,
    priceMinDisplay,
    priceMaxDisplay,
    priceDisplay,
    priceCurrency,
    faceCurrency,
    country,
    regions,
  ]
);
  const promo = useMemo(() => {
    const subTypesForPromo = detailsArray ? subTypes || [] : details?.subTypes || subTypes || [];
    const countryForPromo = detailsArray ? "" : details?.country || country || "";
    return getActivePromo(subTypesForPromo, countryForPromo);
  }, [details, detailsArray, subTypes, country]);

  const priceUSDNum = useMemo(() => {
    if (isRange && Number.isFinite(selectedAmount)) {
      return (priceCurrency || "USD").toUpperCase() === "USD" ? selectedAmount : null;
    }
    const n = Number(priceUSD);
    return Number.isFinite(n) ? n : null;
  }, [isRange, selectedAmount, priceUSD, priceCurrency]);

  const safeFeePercent = useMemo(() => {
    const n = Number(feePercent);
    return Number.isFinite(n) ? n : 0;
  }, [feePercent]);

  const safePiRate = useMemo(() => {
    const n = Number(piRate);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [piRate]);

  const providedBasePI = useMemo(() => {
    const n = Number(basePI);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [basePI]);

  const providedTotalPI = useMemo(() => {
    const n = Number(totalPI);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [totalPI]);

  const computedBasePI = useMemo(
    () => (safePiRate && priceUSDNum != null ? priceUSDNum / safePiRate : providedBasePI),
    [safePiRate, priceUSDNum, providedBasePI]
  );

  const computedTotalPI = useMemo(
    () =>
      computedBasePI != null
        ? computedBasePI + (computedBasePI * safeFeePercent) / 100
        : providedTotalPI,
    [computedBasePI, safeFeePercent, providedTotalPI]
  );

  const baseText = Number.isFinite(computedBasePI) ? computedBasePI.toFixed(4) : "—";
  const totalText = Number.isFinite(computedTotalPI) ? computedTotalPI.toFixed(4) : "—";
  const isReady = Number.isFinite(computedBasePI) && Number.isFinite(computedTotalPI);

  const priceText = useMemo(() => {
    if (isRange && Number.isFinite(selectedAmount)) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: (priceCurrency || "USD").toUpperCase(),
      }).format(selectedAmount);
    }

    if (isRange && priceMinDisplay && priceMaxDisplay) {
      return `${priceMinDisplay} – ${priceMaxDisplay}`;
    }

    if (priceDisplay) return priceDisplay;

    return priceUSDNum != null
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "USD",
        }).format(priceUSDNum)
      : "—";
  }, [isRange, selectedAmount, priceCurrency, priceMinDisplay, priceMaxDisplay, priceDisplay, priceUSDNum]);

  const handleCardClick = (e) => {
    setIsDetailsVisible((v) => !v);
    onClick?.({ offerId, name, productType, topupSubtype }, e);
  };

  return (
    <div className="product-card mb-4 w-full overflow-x-hidden rounded-2xl border border-border bg-surface p-4 text-textPrimary shadow-sm transition-all hover:-translate-y-[1px] hover:border-primary/60 hover:shadow-lg">
      <div
        onClick={handleCardClick}
        className={onClick ? "cursor-pointer select-none" : "cursor-default"}
      >
        <Header displayTitle={displayTitle} offerId={offerId} detailHref={detailHref} />

        <Badges
          isVoucher={isVoucher}
          isVisa={isVisa}
          isVisaAwards={isVisaAwards}
          isRange={isRange}
          subTypes={subTypes}
          promo={promo}
        />

        <div className="mb-3 flex justify-center">
          <img
            src={imgSrc}
            alt={name || displayTitle}
            loading="lazy"
            className="pointer-events-none h-24 w-24 rounded-md object-contain"
          />
        </div>

        {country && isVoucher && (
          <div className="mb-2" onClick={(e) => e.stopPropagation()}>
            <Link
              href={`/vouchers/country/${toA2(country).toLowerCase()}`}
              className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:underline"
            >
              🌍{countryName(country)}
            </Link>
          </div>
        )}

        {isEsim && (detailsArray?.length > 0 || esimsAutoDetails?.length > 0) && (
          <div className="mb-3 rounded-xl border border-border bg-surfaceMuted px-3 py-2">
            <DetailRows rows={detailsArray?.length ? detailsArray : esimsAutoDetails} />
          </div>
        )}

        {isRange && (
          <AmountSelector
            min={min}
            max={max}
            step={Number(priceStep) || 1}
            priceCurrency={priceCurrency}
            selectedAmount={selectedAmount}
            setSelectedAmount={setSelectedAmount}
            priceMinDisplay={priceMinDisplay}
            priceMaxDisplay={priceMaxDisplay}
          />
        )}

        <PriceBlock
          priceText={priceText}
          isRange={isRange}
          priceCurrency={priceCurrency}
          localValueDisplay={localValueDisplay}
          localCurrency={localCurrency}
        />

        <ExpandedDetails
          isDetailsVisible={isDetailsVisible}
          productType={productType}
          recipientGets={recipientGets}
          sendCurrency={sendCurrency}
          topupSubtype={topupSubtype}
          isEsim={isEsim}
          detailsArray={detailsArray}
          voucherAutoDetails={voucherAutoDetails}
          shortNotes={shortNotes}
          notes={notes}
          showFullNotes={showFullNotes}
          setShowFullNotes={setShowFullNotes}
          country={country}
          roamingCount={roamingCount}
          roamingCountries={roamingCountries}
          regions={regions}
        />

        {isDetailsVisible && showCryptoHint && priceUSDNum != null && (
          <CryptoHint priceUSDNum={priceUSDNum} />
        )}

        <PiSummary
          isDetailsVisible={isDetailsVisible}
          isReady={isReady}
          totalText={totalText}
          baseText={baseText}
          safeFeePercent={safeFeePercent}
          priceUSDNum={priceUSDNum}
        />
      </div>
    </div>
  );
}